// v2/engines/permission-engine.ts — Pure permission evaluation functions

import { Action, ApplicationState, Role } from '../shared/types/domain';
import { PermissionMatrix } from '../config/types';
import { DEFAULT_ROLE_PERMISSIONS } from '../config/permissions.config';

/**
 * Evaluates whether an action is allowed for a given context.
 * When applicationState is provided, looks up the (role, state) entry in the matrix.
 * When applicationState is undefined, falls back to default role permissions.
 * Returns false (deny) when no matching entry exists.
 */
export function isActionAllowed(
  config: PermissionMatrix,
  role: Role,
  action: Action,
  applicationState?: ApplicationState
): boolean {
  if (applicationState === undefined) {
    const defaultActions = DEFAULT_ROLE_PERMISSIONS[role];
    if (!defaultActions) {
      return false;
    }
    return defaultActions.includes(action);
  }

  const roleEntry = config[role];
  if (!roleEntry) {
    return false;
  }

  const stateActions = roleEntry[applicationState];
  if (!stateActions) {
    return false;
  }

  return stateActions.includes(action);
}

/**
 * Returns all allowed actions for a given context.
 * When applicationState is provided, returns the actions from the matrix entry.
 * When applicationState is undefined, returns default role permissions.
 * Returns an empty array when no matching entry exists.
 */
export function getAllowedActions(
  config: PermissionMatrix,
  role: Role,
  applicationState?: ApplicationState
): Action[] {
  if (applicationState === undefined) {
    const defaultActions = DEFAULT_ROLE_PERMISSIONS[role];
    return defaultActions ? [...defaultActions] : [];
  }

  const roleEntry = config[role];
  if (!roleEntry) {
    return [];
  }

  const stateActions = roleEntry[applicationState];
  return stateActions ? [...stateActions] : [];
}
